/**
 * A number in an editor's property panel, with an inline label and a precision; an `angle` wraps
 * past ±360 instead of being clamped. Wraps the standalone NumberInput for react-hook-form.
 */
import React, { useState } from "react"
import {
  FormControl,
  FormHelperText,
  FormLabel,
  Box,
  LayoutProps,
} from "@chakra-ui/react"
import {
  Control, FieldValues, FieldPath, FieldPathValue, useController,
} from "react-hook-form"
import NumberInput from "components/elements/NumberInput"
import { MIXED, useIsMixed } from "./MixedValues"

type Props<T extends FieldValues> = {
  name: FieldPath<T>
  control: Control<T, object>
  label?: string
  disabled?: boolean
  error?: boolean
  helperText?: string
  min?: number
  max?: number
  step?: number
  defaultValue?: FieldPathValue<T, FieldPath<T>>
  w?: LayoutProps["w"]
  precision?: number
  inlineLabel?: boolean
  size?: "sm" | "md" | "lg"
  /** Degrees: not clamped to min and max, wrapped past ±360 */
  angle?: boolean
}

const NumberProperty = <T extends FieldValues, >({
  name,
  control,
  label,
  disabled,
  error,
  helperText,
  min,
  max,
  step = 1,
  defaultValue,
  w,
  precision,
  inlineLabel = false,
  size = "md",
  angle = false,
}: Props<T>) => {
  const { field } = useController<T, FieldPath<T>>({ name, control, defaultValue })
  const placeholder = useIsMixed(name) ? MIXED : undefined
  // What's typed shows as typed ("-", "1.") until the field is left; a number is applied as soon
  // as there's one
  const [draft, setDraft] = useState<string | null>(null)
  const value = draft ?? (placeholder ? "" : field.value ?? "")
  const endDraft = () => setDraft(null)

  const handleChange = (typed: string) => {
    setDraft(typed)
    if (["", "-", ".", "-."].includes(typed)) return

    let number = Number(typed)
    if (Number.isNaN(number)) {
      number = min ?? 0
    } else {
      if (min !== undefined && number < min && !angle) {
        number = min
      }
      if (max !== undefined && number > max && !angle) {
        number = max
      }

      if (angle && Math.abs(number) > 360) {
        const sign = number >= 0 ? 1 : -1
        number = sign * (Math.abs(number) % 360)
      }
    }
    if (precision !== undefined) {
      number = Number(number.toFixed(precision))
    }
    field.onChange(number)
  }

  const formatValue = (val: string | number) => {
    if (draft !== null) return draft
    // Empty stays empty: several elements that don't share the value
    if (val === "") return ""
    const num = Number(val)
    if (Number.isNaN(num)) return ""

    if (precision !== undefined) {
      const rounded = Number(num.toFixed(precision))

      return String(rounded)
    }

    return String(val)
  }

  const parseValue = (val: string) => val.replace(/[^\d.-]/g, "")

  if (inlineLabel && label) {
    return (
      <FormControl isDisabled={disabled} isInvalid={error}>
        <NumberInput
          label={label}
          value={value}
          onChange={handleChange}
          onBlur={endDraft}
          clampValueOnBlur={false}
          min={min}
          max={max}
          step={step}
          precision={precision}
          size={size}
          format={formatValue}
          parse={parseValue}
          placeholder={placeholder}
        />
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    )
  }

  return (
    <FormControl
      isDisabled={disabled}
      display="flex"
      alignItems="center"
      isInvalid={error}
    >
      {label && (
        <FormLabel mb={0} fontSize="sm">
          {label}
        </FormLabel>
      )}
      <Box flex={1}>
        <NumberInput
          value={value}
          onChange={handleChange}
          onBlur={endDraft}
          clampValueOnBlur={false}
          min={min}
          max={max}
          step={step}
          precision={precision}
          size={size}
          format={formatValue}
          parse={parseValue}
          w={w}
          placeholder={placeholder}
        />
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </Box>
    </FormControl>
  )
}

export default NumberProperty
